import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../../components/ui/alert-dialog';
import { toast } from 'sonner';
import { Plus, UserCog, Shield, User, Ban, KeyRound, Hash } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const UsersPage = () => {
  const [users, setUsers] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [resetTarget, setResetTarget] = useState(null);
  const [newPasscode, setNewPasscode] = useState('');
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    role: 'operator'
  });

  useEffect(() => { fetchUsers(); }, []);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`);
      setUsers(response.data);
    } catch (error) {
      toast.error('Failed to load users');
    }
  };

  const openModal = (user = null) => {
    if (user) {
      setEditingUser(user);
      setFormData({
        username: user.username,
        password: '',
        role: user.role
      });
    } else {
      setEditingUser(null);
      setFormData({ username: '', password: '', role: 'operator' });
    }
    setIsModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingUser) {
        const updateData = {
          username: formData.username,
          role: formData.role
        };
        if (formData.password) updateData.password = formData.password;
        await axios.put(`${API}/users/${editingUser.id}`, updateData);
        toast.success('User updated');
      } else {
        if (!formData.username) { toast.error('Username is required'); return; }
        await axios.post(`${API}/auth/register`, formData);
        toast.success('User created');
      }
      setIsModalOpen(false);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save user');
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await axios.delete(`${API}/users/${deleteTarget.id}`);
      toast.success(`User '${deleteTarget.username}' deleted`);
      setDeleteTarget(null);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const handleResetPasscode = async () => {
    if (!resetTarget) return;
    const code = newPasscode || resetTarget.user_code;
    try {
      await axios.post(`${API}/auth/reset-passcode/${resetTarget.id}`, { new_passcode: code });
      toast.success(`Passcode reset for ${resetTarget.username}`);
      setResetTarget(null);
      setNewPasscode('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset passcode');
    }
  };

  return (
    <div data-testid="users-page" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold text-slate-900">Users</h1>
          <p className="text-slate-500 mt-1 font-body">Manage operators and administrators</p>
        </div>
        <Button data-testid="add-user-btn" onClick={() => openModal()}>
          <Plus className="w-4 h-4 mr-2" /> Add User
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {users.map((user) => (
          <div key={user.id} data-testid={`user-card-${user.id}`}
            className="bg-white rounded-lg border p-4 flex flex-col border-slate-200">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                  user.role === 'admin' ? 'bg-purple-100' : user.role === 'chef' ? 'bg-amber-100' : 'bg-blue-100'
                }`}>
                  {user.role === 'admin' ? (
                    <Shield className="w-5 h-5 text-purple-600" />
                  ) : (
                    <User className={`w-5 h-5 ${user.role === 'chef' ? 'text-amber-600' : 'text-blue-600'}`} />
                  )}
                </div>
                <div>
                  <h3 className="font-medium text-slate-900">{user.username}</h3>
                  <p className="text-sm text-slate-500 capitalize">{user.role}</p>
                </div>
              </div>
              {user.user_code && (
                <div className="flex items-center gap-1 px-2.5 py-1 bg-slate-100 rounded-lg" data-testid={`user-code-${user.id}`}>
                  <Hash className="w-3 h-3 text-slate-400" />
                  <span className="text-sm font-mono font-semibold text-slate-700">{user.user_code}</span>
                </div>
              )}
            </div>

            <div className="mt-4 pt-4 border-t border-slate-100 space-y-2 flex-1">
              {user.created_at && (
                <p className="text-xs text-slate-400">
                  Created: {new Date(user.created_at).toLocaleDateString()}
                </p>
              )}
            </div>

            <div className="mt-4 flex gap-2">
              <Button variant="outline" size="sm" className="flex-1"
                data-testid={`edit-user-${user.id}`} onClick={() => openModal(user)}>
                <UserCog className="w-4 h-4 mr-1" /> Edit
              </Button>
              <Button variant="outline" size="sm"
                data-testid={`reset-passcode-${user.id}`}
                onClick={() => { setResetTarget(user); setNewPasscode(''); }}>
                <KeyRound className="w-4 h-4" />
              </Button>
              {user.username !== 'admin' && (
                <Button variant="outline" size="sm" className="text-red-500 hover:text-red-600"
                  data-testid={`delete-user-${user.id}`} onClick={() => setDeleteTarget(user)}>
                  <Ban className="w-4 h-4" />
                </Button>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* User Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent data-testid="user-modal">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">
              {editingUser ? 'Edit User' : 'Add User'}
            </DialogTitle>
            <DialogDescription className="sr-only">
              {editingUser ? 'Edit user details' : 'Create a new user account'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="username">Display Name</Label>
              <Input id="username" data-testid="user-username-input"
                value={formData.username}
                onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                required />
              {!editingUser && (
                <p className="text-xs text-slate-500">A numeric user code will be auto-assigned</p>
              )}
            </div>

            {editingUser && (
              <div className="p-3 bg-slate-50 rounded-lg">
                <Label className="text-sm text-slate-600">User Code</Label>
                <p className="text-xl font-mono font-bold text-slate-900">{editingUser.user_code}</p>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="role">Role</Label>
              <Select value={formData.role}
                onValueChange={(value) => setFormData({ ...formData, role: value })}>
                <SelectTrigger data-testid="user-role-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="operator">Operator</SelectItem>
                  <SelectItem value="chef">Chef</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsModalOpen(false)}>Cancel</Button>
              <Button type="submit" data-testid="save-user-btn">
                {editingUser ? 'Save Changes' : 'Add User'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete User?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to permanently delete "{deleteTarget?.username}" ({deleteTarget?.user_code})?
              This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-500 hover:bg-red-600"
              data-testid="confirm-delete-user">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Reset Passcode Dialog */}
      <Dialog open={!!resetTarget} onOpenChange={() => setResetTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reset Passcode</DialogTitle>
            <DialogDescription>
              Reset passcode for {resetTarget?.username} ({resetTarget?.user_code})
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label>New Passcode</Label>
              <Input data-testid="reset-passcode-input" type="text" inputMode="numeric" pattern="[0-9]*"
                placeholder={`Default: ${resetTarget?.user_code || '----'}`}
                value={newPasscode}
                onChange={(e) => setNewPasscode(e.target.value.replace(/\D/g, ''))} />
              <p className="text-xs text-slate-500 mt-1">Leave blank to reset to their user code ({resetTarget?.user_code})</p>
            </div>
            <Button data-testid="confirm-reset-passcode" className="w-full" onClick={handleResetPasscode}>
              Reset Passcode
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UsersPage;
